import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useAuth } from "@/core/auth/contexts/AuthContext";
import CompaniesTable from "../components/companies-table";
import { CompanyTableData } from "../components/companies-table-columns";
import { CreateCompanyDialog } from "../components/create-company-dialog";
import { EditCompanyDialog } from "../components/edit-company-dialog";
import { DeleteCompanyDialog } from "../components/delete-company-dialog";
import { toast } from "sonner";

function CompaniesPage() {
  const { user } = useAuth();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<CompanyTableData | null>(null);

  // Check if user is super-admin
  if (user?.role !== "super-admin") {
    return (
      <div className="w-full p-6 max-w-7xl mx-auto">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-muted-foreground">
            Only super-admins can access the companies management page.
          </p>
        </div>
      </div>
    );
  }

  const handleEdit = (company: CompanyTableData) => {
    setSelectedCompany(company);
    setEditDialogOpen(true);
  };

  const handleDelete = (company: CompanyTableData) => {
    setSelectedCompany(company);
    setDeleteDialogOpen(true);
  };

  const handleCreateSuccess = () => {
    setCreateDialogOpen(false);
    toast.success("Company created successfully");
  };

  const handleEditSuccess = () => {
    setEditDialogOpen(false);
    setSelectedCompany(null);
    toast.success("Company updated successfully");
  };

  const handleDeleteSuccess = () => {
    setDeleteDialogOpen(false);
    setSelectedCompany(null);
    toast.success("Company deleted successfully");
  };

  return (
    <div className="w-full p-6 max-w-7xl mx-auto space-y-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">Companies</h1>
          <p className="text-muted-foreground">
            Manage companies and their administrators
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Company
        </Button>
      </div>

      {/* Companies Table */}
      <CompaniesTable
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {/* Create Company Dialog */}
      <CreateCompanyDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={handleCreateSuccess}
      />

      {/* Edit Company Dialog */}
      {selectedCompany && (
        <EditCompanyDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          company={selectedCompany}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Delete Company Dialog */}
      {selectedCompany && (
        <DeleteCompanyDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          company={selectedCompany}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </div>
  );
}

export default CompaniesPage;
